import React from 'react';
import { Row } from 'reactstrap';
import * as AiIcons from 'react-icons/ai';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';
import Navi from '../components/nav';
import Const from '../components/Const';
import DefaultUserPicture from '../images/default_user.jpg';
import { getUserJsonFromDBwithCookie, messageModalWindow, sleepingCat, getUserPictureClassName } from '../components/Utils.js';
import { pageScrollToTop } from '../components/ScrollUtils.js';
import { isDesktop } from '../utils/AppUtils.js';

// ---------------------------------------------------------------------------
// Sample feed. Swap buildSampleNotifications() for a real service call
// (see getData/GetComments.js etc. for the invokeService() pattern) once
// a notifications endpoint exists — the rest of the page only depends on
// the shape below: { id, type, actorName, actorPicture, userPictureType,
// text, thumbnail, createdAt, read }.
// ---------------------------------------------------------------------------
function buildSampleNotifications() {
	const now = Date.now();
	const minutes = (n) => n * 60 * 1000;
	const hours = (n) => n * 60 * minutes(1);
	const days = (n) => n * 24 * hours(1);

	return [
		{
			id: 1,
			type: Const.NOTIF_TYPE_CONCIERGE,
			actorName: 'Medeata AI',
			text: 'made you a highlight reel from this week — take a look ✨',
			createdAt: now - minutes(12),
			read: false
		},
		{
			id: 2,
			type: Const.NOTIF_TYPE_LIKE,
			actorName: 'Rosa Kim',
			text: 'liked your post',
			thumbnail: null,
			createdAt: now - minutes(40),
			read: false
		},
		{
			id: 3,
			type: Const.NOTIF_TYPE_COMMENT,
			actorName: 'Devon Blake',
			text: 'commented: "This is beautiful, where was this taken?"',
			createdAt: now - hours(3),
			read: false
		},
		{
			id: 4,
			type: Const.NOTIF_TYPE_FOLLOW,
			actorName: 'Priya Natarajan',
			text: 'started following you',
			createdAt: now - hours(6),
			read: true
		},
		{
			id: 5,
			type: Const.NOTIF_TYPE_MENTION,
			actorName: 'Sam Okafor',
			text: 'mentioned you in a comment',
			createdAt: now - days(1),
			read: true
		},
		{
			id: 6,
			type: Const.NOTIF_TYPE_LIKE,
			actorName: 'Ingrid Voss',
			text: 'and 12 others liked your post',
			createdAt: now - days(2),
			read: true
		},
		{
			id: 7,
			type: Const.NOTIF_TYPE_CONCIERGE,
			actorName: 'Medeata AI',
			text: 'Your weekly recap is ready — 3 new followers, 42 likes.',
			createdAt: now - days(6),
			read: true
		}
	];
}

function timeAgo(timestamp) {
	const diffMs = Date.now() - timestamp;
	const minute = 60 * 1000;
	const hour = 60 * minute;
	const day = 24 * hour;

	if (diffMs < minute) {
		return 'just now';
	}
	if (diffMs < hour) {
		const m = Math.floor(diffMs / minute);
		return m + 'm';
	}
	if (diffMs < day) {
		const h = Math.floor(diffMs / hour);
		return h + 'h';
	}
	const d = Math.floor(diffMs / day);
	return d + 'd';
}

function groupLabelFor(timestamp) {
	const diffMs = Date.now() - timestamp;
	const day = 24 * 60 * 60 * 1000;

	if (diffMs < day) {
		return 'Today';
	}
	if (diffMs < 7 * day) {
		return 'This week';
	}
	return 'Earlier';
}

function iconForType(type) {
	switch (type) {
		case Const.NOTIF_TYPE_LIKE:
			return <AiIcons.AiFillHeart className="notif-icon notif-icon-like" />;
		case Const.NOTIF_TYPE_COMMENT:
			return <FiIcons.FiMessageCircle className="notif-icon notif-icon-comment" />;
		case Const.NOTIF_TYPE_FOLLOW:
			return <FiIcons.FiUserPlus className="notif-icon notif-icon-follow" />;
		case Const.NOTIF_TYPE_MENTION:
			return <BsIcons.BsAt className="notif-icon notif-icon-mention" />;
		case Const.NOTIF_TYPE_CONCIERGE:
		default:
			return <AiIcons.AiFillStar className="notif-icon notif-icon-concierge" />;
	}
}

export default class Notifications extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			modal: false,
			messageModal: false,
			loginUser: props.location && props.location.params ? props.location.params.loginUser : null,
			notifications: null,
			filter: Const.NOTIF_FILTER_ALL,
			//
			serviceInvocationStatus: Const.SERVICE_IS_NOT_INVOKED_YET,
			serviceStatusCode: Const.INIT_VALUE,
			serviceErrorLevel: Const.NO_ERROR_LEVEL,
			serviceInvocationError: false,
			flag: Const.INIT_VALUE
		};
		this.allowToggle = true;
		this.markAllAsRead = this.markAllAsRead.bind(this);
		this.showAll = this.showAll.bind(this);
		this.showUnreadOnly = this.showUnreadOnly.bind(this);
		pageScrollToTop();
	}

	async componentDidMount() {
		let loginUser = this.state.loginUser;
		let result;

		if (!loginUser) {
			result = await getUserJsonFromDBwithCookie(this);
			if (this.state.serviceErrorLevel === Const.NO_ERROR_LEVEL) {
				loginUser = result;
			}
		}
		if (loginUser) {
			this.setState({ loginUser: loginUser, notifications: buildSampleNotifications() });
		}
	}

	markAsRead(id) {
		const notifications = this.state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n));

		this.setState({ notifications: notifications });
	}

	markAllAsRead() {
		const notifications = this.state.notifications.map((n) => ({ ...n, read: true }));

		this.setState({ notifications: notifications });
	}

	showAll() {
		if (this.allowToggle) {
			this.setState({ filter: Const.NOTIF_FILTER_ALL });
		}
	}

	showUnreadOnly() {
		if (this.allowToggle) {
			this.setState({ filter: Const.NOTIF_FILTER_UNREAD });
		}
	}

	getFilteredNotifications() {
		const { notifications, filter } = this.state;

		if (!notifications) {
			return [];
		}
		if (filter === Const.NOTIF_FILTER_UNREAD) {
			return notifications.filter((n) => !n.read);
		}
		return notifications;
	}

	groupNotifications(list) {
		const groups = {};
		const order = ['Today', 'This week', 'Earlier'];

		order.forEach((label) => { groups[label] = []; });
		list.forEach((n) => {
			const label = groupLabelFor(n.createdAt);

			groups[label].push(n);
		});
		return order.filter((label) => groups[label].length > 0).map((label) => ({ label, items: groups[label] }));
	}

	renderNotificationRow(n) {
		const avatarObj = { userPictureType: '0' };
		const rowClassName = 'notif-row' + (n.read ? '' : ' notif-row-unread') + (n.type === Const.NOTIF_TYPE_CONCIERGE ? ' notif-row-concierge' : '');

		return (
			<div key={n.id} className={rowClassName} onClick={() => this.markAsRead(n.id)} role="none">
				<div className="notif-icon-wrap">
					{iconForType(n.type)}
				</div>

				<div className={getUserPictureClassName(avatarObj) + ' notif-avatar'}>
					<img src={n.actorPicture ? n.actorPicture : DefaultUserPicture} alt="Avatar" />
				</div>

				<div className="notif-body">
					<p className="notif-text mb-0">
						<span className="notif-actor">{n.actorName}</span> {n.text}
					</p>
					<span className="notif-time">{timeAgo(n.createdAt)}</span>
				</div>

				{
					!n.read &&
					<span className="notif-unread-dot" aria-label="unread" />
				}
			</div>
		);
	}

	renderEmptyState() {
		return (
			<div className="notif-empty">
				<BsIcons.BsBell className="notif-empty-icon" />
				<p className="notif-empty-title mb-0">You're all caught up</p>
				<p className="notif-empty-sub mb-0">New likes, comments and AI concierge moments will show up here.</p>
			</div>
		);
	}

	render() {
		const { serviceErrorLevel, loginUser, header, text, notifications, filter } = this.state;
		const fixedPosClassName = isDesktop() ? 'posFixed ' : '';
		const className = fixedPosClassName + 'after-app-wrap-center-div-gotop safeAreaForPages';

		if (!loginUser || !notifications) {
			if (serviceErrorLevel !== Const.NO_ERROR_LEVEL) {
				return messageModalWindow(this, header, text);
			}
			return (
				<>
					{sleepingCat()}
				</>
			);
		}

		loginUser.whereIam = Const.I_AM_IN_NOTIFICATIONS;

		const filtered = this.getFilteredNotifications();
		const grouped = this.groupNotifications(filtered);
		const hasUnread = notifications.some((n) => !n.read);

		return (
			<>
				<Navi loginUser={loginUser} />
				<div id="main" className={className}>
					<Row>
						<div className="notif-page">
							<div className="notif-header">
								<h3 className="notif-title mb-0">Notifications</h3>
								{
									hasUnread &&
									<button type="button" className="notif-mark-all pointer-view" onClick={this.markAllAsRead}>
										Mark all as read
									</button>
								}
							</div>

							<div className="notif-tabs">
								<button
									type="button"
									className={'notif-tab pointer-view' + (filter === Const.NOTIF_FILTER_ALL ? ' notif-tab-active' : '')}
									onClick={this.showAll}
								>
									All
								</button>
								<button
									type="button"
									className={'notif-tab pointer-view' + (filter === Const.NOTIF_FILTER_UNREAD ? ' notif-tab-active' : '')}
									onClick={this.showUnreadOnly}
								>
									Unread
								</button>
							</div>

							{
								grouped.length === 0
									? this.renderEmptyState()
									: grouped.map((group) => (
										<div className="notif-group" key={group.label}>
											<h5 className="notif-group-label">{group.label}</h5>
											<div className="notif-card">
												{group.items.map((n) => this.renderNotificationRow(n))}
											</div>
										</div>
									))
							}
						</div>
					</Row>
				</div>
			</>
		);
	}
}
